<template>
    <modal v-if="project"
           id="projects-modal"
           size="lg"
           @close="close()"
           :show="show">

        <template #title>Project Settings</template>

        <div class="row g-3 align-items-center">
            <div class="col-3 mb-3 text-right">
                <label for="project-name"
                       class="col-form-label">Project Name</label>
            </div>
            <div class="col-9 mb-3">
                <input type="text"
                       id="project-name"
                       class="form-control form-control-lg"
                       v-model="project.name"
                       @change="updateSettings"
                       aria-describedby="project-name">
            </div>
        </div>
        <div class="row g-3 align-items-center">
            <div class="col-3 mb-3 text-right">
                <label for="project-classroom"
                       class="col-form-label">Classroom</label>
            </div>
            <div class="col-9 mb-3">
                <input type="text"
                       id="project-classroom"
                       class="form-control form-control-lg"
                       v-model="project.classroom"
                       @change="updateSettings"
                       aria-describedby="project-classroom">
            </div>
        </div>

        <hr class="mt-0">

        <div class="row g-3 align-items-center">
            <div class="col-3 mb-3 text-right">
                <label for="project-theme"
                       class="col-form-label">Goal</label>
            </div>
            <div class="col-9 mb-3">
                <select id="project-theme"
                        class="form-control"
                        v-model="project.theme"
                        @change="updateSettings"
                        aria-describedby="project-theme">
                    <option :value="theme.id" v-for="theme in themes">{{ theme.name }}</option>
                </select>
            </div>
        </div>

        <div class="row g-3 align-items-center">
            <div class="col-3 text-right mb-3">
                <label for="project-learning-path"
                       class="col-form-label">Source Learning Path</label>
            </div>
            <div class="col-6 mb-3">
                <select id="project-learning-path"
                        class="form-control"
                        v-model="project.learning_path.parent_id"
                        disabled
                        aria-describedby="project-learning-path">
                    <option v-for="path in learning_paths"
                            :value="path.id">{{ path.name }}</option>
                </select>

            </div>
            <div class="col-3 mb-3">
                <button type="button" class="btn btn-danger btn-block" disabled>Customize</button>
            </div>
        </div>

        <div class="row g-3 align-items-center">
            <div class="offset-3 col-9 mb-3">
                <p class="text-danger mb-0">
                    Warning: Switching or Customizing learning path is a destructive
                    process and will remove any existing project & canvas data that
                    are not in common.
                </p>
            </div>
        </div>

        <div class="row g-3 align-items-center" v-if="is_admin">
            <div class="col-3 mb-3 text-right">
                <label for="project-editable-by"
                       class="col-form-label">Editable By</label>
            </div>
            <div class="col-9 mb-3">
                <select class="form-control"
                        id="path-editable-by"
                        v-model="meta.editable_by"
                        @change="updateSettings"
                >
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                </select>
            </div>
        </div>

        <div class="row g-3 align-items-center">
            <div class="col-3 text-right mb-3">
                <label for="project-unlock-mission"
                       class="col-form-label">Unlock Stage Mission</label>
            </div>
            <div class="col-9 mb-3">
                <select id="project-unlock-mission"
                        class="form-control"
                        v-model="meta.unlock_mission"
                        @change="updateSettings"
                        aria-describedby="project-unlock-mission">
                    <option v-for="(option, option_id) in unlock_mission_options"
                            :key="option_id"
                            :value="option_id">
                        {{ option }}
                    </option>
                </select>

            </div>
        </div>

        <div class="row g-3 align-items-center">
            <div class="col-3 text-right mb-3">
                <label for="project-unlock-stage"
                       class="col-form-label">Unlock Next Stage</label>
            </div>
            <div class="col-9 mb-3">
                <select id="project-unlock-stage"
                        class="form-control"
                        v-model="meta.unlock_stage"
                        @change="updateSettings"
                        aria-describedby="project-unlock-stage">
                    <option v-for="(option, option_id) in unlock_stage_options"
                            :key="option_id"
                            :value="option_id">
                        {{ option }}
                    </option>
                </select>

            </div>
        </div>

        <template #footer>
            <state-button v-if="showDelete"
                          type="button"
                          class="btn btn-danger mr-auto"
                          :disabled="is_deleting"
                          :state="is_deleting ? 'busy' : ''"
                          @click.prevent.stop="remove( project )">
                Delete
            </state-button>
            <state-button v-if="showClone"
                          :state="is_cloning ? 'busy' : ''"
                          :disabled="is_cloning"
                          :spinner-dark="true"
                          class="btn btn-outline-dark ml-auto"
                          @click.prevent.stop="cloneProject()">Clone Project</state-button>
            <button type="button" class="btn btn-dark" @click="close()">Close</button>
        </template>
    </modal>
</template>

<script>
import themes from "@/DATA/themes";
import Modal from "@/Components/Common/Modal";
import unlock_mission_options from '@/DATA/unlock_mission_options';
import unlock_stage_options from '@/DATA/unlock_stage_options';
import API from "@/API";
import stages from "@/DATA/stages";
import StateButton from "@/Components/Common/StateButton";

export default {
    name: "ProjectSettingsModal",
    components: {StateButton, Modal},

    props: ['project', 'show', 'showDelete', 'showClone'],
    emits: ['close', 'deleted', 'cloned'],

    data(){
        return {
            is_deleting: false,
            is_cloning: false,
            learning_paths: [],
            themes,
            unlock_mission_options,
            unlock_stage_options,
            available_stages: stages,
        };
    },



    computed: {
        meta: {
            get(){
                const meta = Array.isArray( this.project.meta ) ? {} : this.project.meta;
                return meta ? meta : {};
            },
            set(meta){
                this.project.meta = meta;
            }
        }
    },


    watch:{
        meta: {
            deep: true,
            handler(changed){
                console.log({changed});
                // this.updateSettings();
            }
        }
    },


    methods: {
        async updateSettings(){
            const project = await API.projects.updateSettings(this.project.id, {
                name: this.project.name,
                classroom: this.project.classroom,
                theme: this.project.theme,
                meta: this.meta,
            });

            this.project.name = project.name;
            this.project.classroom = project.classroom;
            this.project.theme = project.theme;
            this.meta = project.meta;
        },

        close(){
            this.$emit('close');
        },

        remove( project ){
            this.$inertia.delete(this.route('project.destroy', {project: project.id, ...this.global_url_params}), {
                onBefore:   () => confirm('Are you sure to delete this project?\n\nThis will destroy all data associated to this project and can\'t be undone.'),
                onStart:    () => this.is_deleting = true,
                onSuccess:  (page) => console.log("Success"),
                onError:    (errors) => console.log({errors}),
                onFinish:   () => this.is_deleting = false,
            });
        },


        async cloneProject(){

            console.log('CLONING...');

            this.is_cloning = true;
            try{
                const project = await API.projects.clone( this.project.id );
                this.$emit('cloned', project);

            } catch (e) {}
            this.is_cloning = false;

            console.log('CLONING END!');
        }


    },

    beforeMount() {
        // if( !this.meta?.unlock_mission ) this.meta.unlock_mission = 'all';
        // if( !this.meta?.unlock_stage ) this.meta.unlock_stage = 'all';
    },


    async mounted() {
        this.learning_paths = await API.learning_paths.bulk();
        console.log({meta: this.meta});
    }

}
</script>

<style scoped>

</style>
